import { logClientSdkDiagnostics } from '@/api/scribeAPIV2';
import {
  useIsClientSdkSelfReportEnabled,
  useIsClientSdkSelfReportKilled,
} from '@/app-shell/flags/shell-flags';
import { useUser } from '@/hooks/query/useQueryUser';
import { logger } from '@/utils/logger/logger';
import { useAreFeatureFlagsResolved } from '@heidi/runtime/feature-flags';
import { ProviderStatus, useOpenFeatureClientStatus } from '@openfeature/react-sdk';
import { useEffect, useRef, useState } from 'react';
import { collectClientSdkStatus } from './collectClientSdkStatus';

const FLAG_SETTLE_TIMEOUT_MS = 10_000;

/**
 * Reports a bounded client diagnostics snapshot after auth.
 *
 * Gating:
 * - `op-scribe-disable-client-sdk-self-report` (default ON) is the fail-closed
 *   kill switch — when killed, nothing reports, including provider ERROR /
 *   settle-timeout paths.
 * - `release-scribe-client-sdk-self-report` enables the happy path.
 * - When the kill switch is OFF, provider ERROR / slow resolve still report so
 *   a blocked Flagsmith route remains diagnosable without the rollout flag.
 */
export function ClientSdkSelfReportSync() {
  const isKilled = useIsClientSdkSelfReportKilled();
  const isEnabled = useIsClientSdkSelfReportEnabled();
  const flagsResolved = useAreFeatureFlagsResolved();
  const flagsProviderStatus = useOpenFeatureClientStatus();
  const { data: userRefId } = useUser((user) => user.user_ref_id);
  const [flagSettleTimedOut, setFlagSettleTimedOut] = useState(false);
  const hasReportedRef = useRef(false);

  useEffect(() => {
    if (flagsResolved) return;

    const timeoutId = window.setTimeout(
      () => setFlagSettleTimedOut(true),
      FLAG_SETTLE_TIMEOUT_MS
    );
    return () => window.clearTimeout(timeoutId);
  }, [flagsResolved]);

  useEffect(() => {
    const shouldReport =
      !isKilled &&
      (isEnabled ||
        flagsProviderStatus === ProviderStatus.ERROR ||
        (!flagsResolved && flagSettleTimedOut));

    if (!userRefId || !shouldReport || hasReportedRef.current) return;

    hasReportedRef.current = true;
    void collectClientSdkStatus({
      flagsProviderStatus: String(flagsProviderStatus),
      flagsResolved,
    })
      .then((report) => logClientSdkDiagnostics(report))
      .catch(() => {
        // The report must not disrupt the app or leak transport details into logs.
        logger.warn('[client-sdk-status] diagnostic report failed', {
          feature: 'client_sdk_self_report',
        });
      });
  }, [
    flagSettleTimedOut,
    flagsProviderStatus,
    flagsResolved,
    isEnabled,
    isKilled,
    userRefId,
  ]);

  return null;
}
